<template>
	<div class="position-relative">
		<highcharts class="highcharts-container" :options="chartOptions" :highcharts="localHc" />
	</div>
</template>

<script setup lang="ts">
import Highcharts, { Options, SeriesOptionsType } from 'highcharts';
import BaseChart from './BaseChartOptions';
import big from 'big.js';
import { computed } from 'vue';
import { currency } from '@filters/shared-filters';
import deepMerge from 'deepmerge';
import { Chart as highcharts } from 'highcharts-vue';
import { IpoPriceData } from 'types/ipo';
import More from 'highcharts/highcharts-more';
More(Highcharts);

interface Props {
	ipoPriceHistory: Array<IpoPriceData>;
	chartHeight?: number;
}

const props = withDefaults(defineProps<Props>(), {
	chartHeight: 300
});

const localHc = Highcharts;

const actualGrowthDataArray = computed((): Array<Record<string, string | number | object | null>> => {
	const actualSeriesData: Array<Record<string, string | number | object | null>> = convertToSeriesArray(
		props.ipoPriceHistory
	);

	return actualSeriesData;
});

const chartOptions = computed((): Options => {
	let localOptions: Options = {};

	const series: Array<SeriesOptionsType> = [
		{
			name: 'iPO Share Price',
			type: 'area',
			data: actualGrowthDataArray.value,
			color: '#517787'
		}
	];

	localOptions = {
		chart: {
			type: 'area',
			height: props.chartHeight
		},
		series,
		xAxis: {
			type: 'datetime',
			tickInterval: 120 * 24 * 3600 * 1000
		},
		yAxis: {
			labels: {
				formatter(): string {
					return currency(this.value.toString(), 0) ?? '';
				}
			},
			gridLineColor: '#F2F1ED',
			gridLineDashStyle: 'LongDash',
			gridLineWidth: 1.25,
			min: 4.0,
			tickInterval: 2.0
		},
		tooltip: {
			enabled: true,
			useHTML: true,
			formatter(): string {
				return `<div class="text-align-center"><strong class="delta">${currency(
					this.y
				)}</strong><br>${Highcharts.dateFormat('%B %e, %Y', this.x)}</div>`;
			}
		},
		plotOptions: {
			area: {
				stacking: 'normal',
				lineWidth: 2,
				marker: {
					symbol: 'circle',
					radius: 4,
					enabled: true
				},
				fillColor: {
					linearGradient: { x1: 1, y1: 0, x2: 0, y2: 1 },
					stops: [
						[0, new Highcharts.Color('#517787').setOpacity(0.2).get('rgba').toString()],
						[1, new Highcharts.Color('#517787').setOpacity(0).get('rgba').toString()]
					]
				},
				fillOpacity: 0.25
			}
		}
	};

	return deepMerge(new BaseChart().getOptions(), localOptions);
});

function convertToSeriesArray(toConvert: Array<IpoPriceData>): Array<Record<string, string | number | object | null>> {
	return toConvert.map((currentObject) => ({
		x: Number(currentObject.dateTime),
		y: Number(big(currentObject.price))
	}));
}
</script>
